/* eslint-disable max-lines-per-function */
import React from "react";
import { Typography } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "components/haven-design/snackbar";
import {
  mergePropertyContractUpdatedIntoList,
  PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
} from "data/graphql/property-contract-updated-subscription";
import {
  ARCHIVE_MUTATION,
  ContractDetailsCard,
  DeletePendingModal,
  PropertyContractsData,
  PROPERTY_CONTRACTS_QUERY,
} from "../../pages/contracts-shared";
import { useContractDetailsActions } from "../../pages/use-contract-details-actions";

type Props = {
  listId?: string;
  propertyName?: string | null;
}

const PropertyContractSettings: React.FC<Props> = ({ listId, propertyName }) => {
  const { openSnackbar } = useSnackbar();
  const { data, loading, error, refetch, subscribeToMore } =
    useQuery<PropertyContractsData>(PROPERTY_CONTRACTS_QUERY, {
      fetchPolicy: "cache-first",
    });
  const [archiving, setArchiving] = React.useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);

  const [archiveMutation] =
    useMutation(ARCHIVE_MUTATION, {
      refetchQueries: [{ query: PROPERTY_CONTRACTS_QUERY }],
      awaitRefetchQueries: true,
    });

  React.useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) =>
        mergePropertyContractUpdatedIntoList(prev, subscriptionData),
    });
    return () => {
      unsubscribe();
    };
  }, [subscribeToMore]);

  const contracts = data?.propertyContracts || [];
  const selectedContract =
    contracts.find(
      (contract) =>
        (listId && contract.interestListId === listId) ||
        (propertyName && contract.propertyName === propertyName),
    ) ||
    null;
  const [resolvingContract, setResolvingContract] = React.useState(false);
  const resolveAttemptedForListId = React.useRef<string | null>(null);

  const {
    busyId,
    signedContractId,
    reminderSentForContractId,
    reminderFailure,
    showSignNow,
    handleResendConfirm,
    handleSignNow,
  } = useContractDetailsActions(selectedContract, refetch);

  React.useEffect(() => {
    if (!listId || loading || selectedContract) {
      if (!listId) {
        resolveAttemptedForListId.current = null;
      }
      setResolvingContract(false);
      return () => {};
    }
    if (resolveAttemptedForListId.current === listId) {
      return () => {};
    }
    resolveAttemptedForListId.current = listId;
    let cancelled = false;
    setResolvingContract(true);
    refetch()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setResolvingContract(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listId, loading, selectedContract, refetch]);

  const handleArchiveConfirm = async () => {
    if (!selectedContract) return;
    setArchiving(true);
    try {
      await archiveMutation({ variables: { id: selectedContract.id } });
      setArchiveConfirmOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to archive";
      openSnackbar({ message, status: "error", duration: 4000 });
    } finally {
      setArchiving(false);
      refetch();
    }
  };

  if (loading || resolvingContract) return <Typography variant="body1">Loading...</Typography>;
  if (error) return <Typography variant="body2" color="error">{error.message}</Typography>;
  if (!selectedContract) return <Typography variant="body1">No contract found for this property.</Typography>;

  return (
    <>
      <ContractDetailsCard
        selectedContract={selectedContract}
        busyId={busyId}
        signedContractId={signedContractId}
        reminderSentForContractId={reminderSentForContractId}
        reminderFailure={reminderFailure}
        archiving={archiving}
        onResend={handleResendConfirm}
        {...(showSignNow ? { onSignNow: handleSignNow } : {})}
        onDeleteDraft={() => setArchiveConfirmOpen(true)}
      />

      <DeletePendingModal
        isOpen={archiveConfirmOpen}
        archiving={archiving}
        onClose={() => setArchiveConfirmOpen(false)}
        onConfirm={handleArchiveConfirm}
      />
    </>
  );
};

export default PropertyContractSettings;
